// Service pour la vérification et synchronisation des abonnements

import { axios } from "@/lib/axios";
import {
  ApiResponse,
  IAPIgetSubscriptionVerificationResult,
  MultipleActiveSubscriptionsParams,
  MultipleActiveSubscriptionsResponse,
  SubscriptionData, // Ensure SubscriptionData includes a 'status' property
  SubscriptionProvider,
} from "@/store/reducers/subscription/subscription.types";
import { AxiosError } from "axios";

interface SubscriptionAPIResponse {
  data: SubscriptionData;
  message: string;
}

export const SubscriptionService = {
  /**
   * Vérifie un abonnement auprès de Stripe ou PayPal
   * @param {string} subscriptionId - Identifiant de l'abonnement
   * @param {string} provider - Fournisseur de paiement (stripe ou paypal)
   * @returns {Promise<SubscriptionData>} Détails de l'abonnement
   */
  verifySubscription: async (
    subscriptionId: string,
    provider: SubscriptionProvider
  ): Promise<SubscriptionData> => {
    try {
      const response = await axios.get<Promise<SubscriptionData>>(
        `/payment-s/bo/api/v1/payments-plans-subscriptions/check/${subscriptionId}`,
        {
          params: {
            type: provider,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Erreur lors de la vérification de l'abonnement:", error);
      const axiosError = error as AxiosError<ApiResponse<any>>;
      throw (
        axiosError.response?.data || {
          success: false,
          message:
            "Une erreur est survenue lors de la vérification de l'abonnement",
        }
      );
    }
  },

  /**
   * Récupère les comptes utilisateurs ayant plusieurs abonnements actifs
   * @param {MultipleActiveSubscriptionsParams} params - Paramètres de recherche et pagination
   * @returns {Promise<MultipleActiveSubscriptionsResponse>} Liste des comptes avec plusieurs abonnements
   */
  getMultipleActiveSubscriptions: async (
    params: MultipleActiveSubscriptionsParams = {}
  ): Promise<MultipleActiveSubscriptionsResponse> => {
    try {
      const response = await axios.get<MultipleActiveSubscriptionsResponse>(
        `/payment-s/bo/api/v1/purchases/subscriptions/multiple-active`,
        {
          params: {
            page: params.page || undefined,
            limit: params.limit || 10,
            // sortBy: params.sortBy || "totalActiveCount",
            // sortOrder: params.sortOrder || "desc",
            // minActiveCount: params.minActiveCount || 2,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des comptes avec plusieurs abonnements:",
        error
      );
      const axiosError = error as AxiosError<ApiResponse<any>>;
      throw (
        axiosError.response?.data || {
          success: false,
          message:
            "Une erreur est survenue lors de la récupération des comptes avec plusieurs abonnements",
        }
      );
    }
  },

  /**
   * Synchronise un abonnement vérifié avec le système interne
   * @param {SubscriptionData} subscriptionData - Données de l'abonnement vérifié
   * @returns {Promise<ApiResponse<any>>} Résultat de la synchronisation
   */
  synchronizeSubscription: async (subscriptionData: {
    subscription_id: string;
    type: string;
  }): Promise<IAPIgetSubscriptionVerificationResult> => {
    try {
      const response = await axios.post(
        `/payment-s/bo/api/v1/payments-plans-subscriptions/sync/${subscriptionData.subscription_id}`,
        {},
        {
          params: {
            type: subscriptionData.type,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la synchronisation de l'abonnement:",
        error
      );
      const axiosError = error as AxiosError<ApiResponse<any>>;
      throw (
        axiosError.response?.data || {
          success: false,
          message:
            "Une erreur est survenue lors de la synchronisation de l'abonnement",
        }
      );
    }
  },
};

export default SubscriptionService;
